import React, { useState } from 'react';
import { t as tr, getLang } from './i18n';

// ── Kreator pierwszego uruchomienia (Warstwa 1 onboardingu) ───────────────────
// Nakładka overlay pokazywana gdy tenant.onboarding_done === false.
// 6 ekranów (0-5): powitanie, sklep, OFERTA, asystent, kod, gotowe.
// Zapis kroków przez ax.patch('/tenants/me', {...}).
// Na końcu ustawia onboarding_done=true i zamyka nakładkę (onDone()).
//
// Props:
//   ax        - instancja axios z baseURL API + tokenem (jak w SettingsTab)
//   settings  - dane tenanta z /tenants/me (widget_token, widget_proactive_msg, ...)
//   onDone    - callback zamykający kreator (ustawia lokalny stan w AIOpen)
//   isMobile  - responsywność

const COLOR = '#15803d';
const COLOR_LIGHT = '#f0fdf4';
const DEFAULT_AVATAR = 'https://aiopen-api.openkontakt.com/avatars/magda_default.png';

export default function OnboardingWizard({ ax, settings, onDone, isMobile, onAskAssistant }: any) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Pola edytowalne w kreatorze (start z wartości domyślnych z Warstwy 2)
  const [shopUrl, setShopUrl] = useState(settings?.shop_api_url || '');
  const [svcLang, setSvcLang] = useState(settings?.service_language || getLang() || 'pl');
  const [greeting, setGreeting] = useState(settings?.widget_proactive_msg || '');
  const [assistantName, setAssistantName] = useState(settings?.widget_assistant_first_name || settings?.widget_assistant_name || 'Magda');

  // ── Ekran Oferty: produkty (feed), wiedza (skan), rekomendacja ──
  const [feedUrl, setFeedUrl] = useState('');
  const [feedState, setFeedState] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [scanState, setScanState] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [scanInfo, setScanInfo] = useState('');
  const [reco, setReco] = useState('');
  const [recoState, setRecoState] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');

  const widgetToken = settings?.widget_token || '';
  const snippet = `<script src="https://aiopen-api.openkontakt.com/widget.js?token=${widgetToken}"></script>`;

  const TOTAL = 6; // ekrany 0..5

  // Zapis pól na bieżąco (po każdym "Dalej") — przerwanie nie kasuje postępu.
  const saveFields = async (fields: any) => {
    try { await ax.patch('/tenants/me', fields); } catch (e) { /* nie blokuj kreatora */ }
  };

  const next = async (fields?: any) => {
    if (fields) { setSaving(true); await saveFields(fields); setSaving(false); }
    setStep(s => Math.min(s + 1, TOTAL - 1));
  };

  const finish = async () => {
    setSaving(true);
    await saveFields({ onboarding_done: true });
    setSaving(false);
    onDone();
  };

  const skip = async () => {
    // Świadome pominięcie też kończy onboarding (nie wraca).
    setSaving(true);
    await saveFields({ onboarding_done: true });
    setSaving(false);
    onDone();
  };

  // ── Akcje ekranu Oferty (opcjonalne, nie blokują kreatora) ──
  const loadFeed = async () => {
    if (!feedUrl.trim()) return;
    setFeedState('loading');
    try {
      await ax.post('/products/feed/url', { url: feedUrl.trim() });
      setFeedState('ok');
    } catch (e) { setFeedState('err'); }
  };

  const scanShop = async () => {
    const domain = (shopUrl || '').trim();
    if (!domain) { setScanState('err'); setScanInfo(tr('onb.offer.scan.needUrl')); return; }
    setScanState('loading');
    try {
      await ax.post('/knowledge/scan', { domain });
      setScanState('ok');
      setScanInfo(tr('onb.offer.scan.started'));
    } catch (e) { setScanState('err'); setScanInfo(tr('onb.offer.scan.err')); }
  };

  const addReco = async () => {
    if (!reco.trim()) return;
    setRecoState('loading');
    try {
      await ax.post('/knowledge/recommendations', { content: reco.trim() });
      setRecoState('ok');
    } catch (e) { setRecoState('err'); }
  };

  const copySnippet = () => {
    try {
      navigator.clipboard.writeText(snippet).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    } catch (e) {
      // fallback dla starszych przeglądarek
      const ta = document.createElement('textarea');
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch (e2) {}
      document.body.removeChild(ta);
    }
  };

  const mailtoInformatyk = () => {
    const subject = encodeURIComponent(tr('onb.mail.subject'));
    const body = encodeURIComponent(tr('onb.mail.body') + '\n\n' + snippet + '\n\n' + tr('onb.mail.hint'));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // ── style ───────────────────────────────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: isMobile ? 12 : 24,
  };
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column', maxHeight: '92vh',
  };
  const headerStyle: React.CSSProperties = {
    padding: isMobile ? '14px 18px' : '18px 24px', borderBottom: '1px solid #eef2f7',
    display: 'flex', alignItems: 'center', gap: 10,
  };
  const bodyStyle: React.CSSProperties = {
    padding: isMobile ? '20px 18px' : '28px 32px', overflowY: 'auto', flex: 1,
  };
  const footer: React.CSSProperties = {
    padding: isMobile ? '14px 18px' : '16px 24px', borderTop: '1px solid #eef2f7',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  };
  const primaryBtn: React.CSSProperties = {
    padding: '11px 22px', borderRadius: 10, border: 'none', background: COLOR,
    color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  };
  const ghostBtn: React.CSSProperties = {
    padding: '8px 12px', borderRadius: 8, border: 'none', background: 'transparent',
    color: '#9ca3af', fontSize: 12, cursor: 'pointer',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #d1d5db',
    fontSize: 14, boxSizing: 'border-box',
  };
  const smallBtn: React.CSSProperties = {
    padding: '9px 14px', borderRadius: 8, border: 'none', background: COLOR,
    color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
  };
  const h1: React.CSSProperties = { fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#111827', margin: '0 0 8px' };
  const sub: React.CSSProperties = { fontSize: 14, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 };
  const label: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', margin: '0 0 6px' };
  const hint: React.CSSProperties = { fontSize: 12, color: '#9ca3af', margin: '6px 0 0', lineHeight: 1.5 };
  // Karta sekcji w ekranie Oferty
  const sectionCard: React.CSSProperties = {
    border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 14,
  };
  const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' };
  const okBadge: React.CSSProperties = { fontSize: 12, color: COLOR, fontWeight: 600, marginTop: 8 };
  const errBadge: React.CSSProperties = { fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 8 };

  // ── pasek postępu (kropki) ────────────────────────────────────────────────────
  const dots = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {Array.from({ length: TOTAL }).map((_, i) => (
        <span key={i} style={{
          width: i === step ? 20 : 8, height: 8, borderRadius: 4,
          background: i === step ? COLOR : (i < step ? '#86efac' : '#e5e7eb'),
          transition: 'all 0.2s',
        }} />
      ))}
    </div>
  );

  // ── ekrany ────────────────────────────────────────────────────────────────────
  const screens = [
    // Ekran 0 — Powitanie
    (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44, margin: '4px 0 12px' }}>👋</div>
        <h1 style={h1}>{tr('onb.s0.title')}</h1>
        <p style={sub}>{tr('onb.s0.subtitle')}</p>
      </div>
    ),
    // Ekran 1 — Połącz sklep
    (
      <div>
        <h1 style={h1}>{tr('onb.s1.title')}</h1>
        <label style={label}>{tr('onb.s1.label')}</label>
        <input style={inputStyle} type="url" placeholder="https://twojsklep.pl"
          value={shopUrl} onChange={e => setShopUrl(e.target.value)} />
        <p style={hint}>{tr('onb.s1.hint')}</p>
        <label style={label}>{tr('onb.s1.servicelang')}</label>
        <select value={svcLang} onChange={e => setSvcLang(e.target.value)}
          style={{width:'100%',marginTop:4,padding:'10px 12px',border:'1px solid #d1d5db',borderRadius:6,fontSize:14,boxSizing:'border-box'}}>
          <option value="pl">Polski</option><option value="en">English</option><option value="de">Deutsch</option><option value="cs">Čeština</option><option value="sk">Slovenčina</option><option value="hu">Magyar</option><option value="ro">Română</option>
        </select>
      </div>
    ),
    // Ekran 2 — OFERTA (produkty + wiedza + rekomendacja)
    (
      <div>
        <h1 style={h1}>{tr('onb.offer.title')}</h1>
        <p style={sub}>{tr('onb.offer.subtitle')}</p>

        {/* Sekcja 1 — Produkty (feed) */}
        <div style={sectionCard}>
          <div style={sectionTitle}>📦 {tr('onb.offer.products.title')}</div>
          <p style={{ ...hint, margin: '0 0 10px' }}>{tr('onb.offer.products.desc')}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...inputStyle, flex: 1 }} type="url" placeholder="https://twojsklep.pl/feed.xml"
              value={feedUrl} onChange={e => { setFeedUrl(e.target.value); setFeedState('idle'); }} />
            <button style={smallBtn} disabled={feedState === 'loading' || !feedUrl.trim()} onClick={loadFeed}>
              {feedState === 'loading' ? '…' : tr('onb.offer.products.btn')}
            </button>
          </div>
          {feedState === 'ok' && <div style={okBadge}>✓ {tr('onb.offer.products.ok')}</div>}
          {feedState === 'err' && <div style={errBadge}>{tr('onb.offer.products.err')}</div>}
          <p style={{ ...hint, marginTop: 8 }}>{tr('onb.offer.products.noFeed')}</p>
          {onAskAssistant && <button onClick={() => onAskAssistant(tr('ask.q.products'))} style={{ marginTop: 8, padding: 0, border: 'none', background: 'transparent', color: '#7c3aed', fontSize: 12, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>{tr('ask.assistant')}</button>}
        </div>

        {/* Sekcja 2 — Wiedza o sklepie (skan) */}
        <div style={sectionCard}>
          <div style={sectionTitle}>📚 {tr('onb.offer.knowledge.title')}</div>
          <p style={{ ...hint, margin: '0 0 10px' }}>{tr('onb.offer.knowledge.desc')}</p>
          <button style={smallBtn} disabled={scanState === 'loading'} onClick={scanShop}>
            {scanState === 'loading' ? '…' : tr('onb.offer.knowledge.btn')}
          </button>
          {scanState === 'ok' && <div style={okBadge}>✓ {scanInfo}</div>}
          {scanState === 'err' && <div style={errBadge}>{scanInfo}</div>}
          {onAskAssistant && <button onClick={() => onAskAssistant(tr('ask.q.knowledge'))} style={{ marginTop: 8, padding: 0, border: 'none', background: 'transparent', color: '#7c3aed', fontSize: 12, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>{tr('ask.assistant')}</button>}
        </div>

        {/* Sekcja 3 — Rekomendacja (pierwsza wskazówka) */}
        <div style={sectionCard}>
          <div style={sectionTitle}>⭐ {tr('onb.offer.reco.title')}</div>
          <p style={{ ...hint, margin: '0 0 10px' }}>{tr('onb.offer.reco.desc')}</p>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder={tr('onb.offer.reco.placeholder')}
            value={reco} onChange={e => { setReco(e.target.value); setRecoState('idle'); }} />
          <button style={{ ...smallBtn, marginTop: 8 }} disabled={recoState === 'loading' || !reco.trim()} onClick={addReco}>
            {recoState === 'loading' ? '…' : tr('onb.offer.reco.btn')}
          </button>
          {recoState === 'ok' && <div style={okBadge}>✓ {tr('onb.offer.reco.ok')}</div>}
          {recoState === 'err' && <div style={errBadge}>{tr('onb.offer.reco.err')}</div>}
          {onAskAssistant && <button onClick={() => onAskAssistant(tr('ask.q.recommendations'))} style={{ marginTop: 8, padding: 0, border: 'none', background: 'transparent', color: '#7c3aed', fontSize: 12, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>{tr('ask.assistant')}</button>}
        </div>
      </div>
    ),
    // Ekran 3 — Poznaj asystenta
    (
      <div>
        <h1 style={h1}>{tr('onb.s2.title')}</h1>
        {/* Podgląd bąbelka powitania */}
        <div style={{ background: COLOR_LIGHT, border: '1px solid #bbf7d0', borderRadius: 12, padding: 14, marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <img src={settings?.widget_avatar_url || DEFAULT_AVATAR} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLOR, marginBottom: 2 }}>{assistantName || 'Magda'}</div>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{greeting || tr('onb.s2.greetingPlaceholder')}</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', margin: '-10px 0 16px' }}>{tr('onb.s2.avatarHint')}</div>
        <label style={label}>{tr('onb.s2.nameLabel')}</label>
        <input style={{ ...inputStyle, marginBottom: 14 }} value={assistantName} onChange={e => setAssistantName(e.target.value)} />
        <label style={label}>{tr('onb.s2.greetingLabel')}</label>
        <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
          value={greeting} onChange={e => setGreeting(e.target.value)} />
      </div>
    ),
    // Ekran 4 — Wgraj kod
    (
      <div>
        <h1 style={h1}>{tr('onb.s3.title')}</h1>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <div style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: 10, padding: '14px 16px', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5 }}>
            {snippet}
          </div>
          <button onClick={copySnippet} style={{ ...primaryBtn, position: 'absolute', top: 8, right: 8, padding: '6px 12px', fontSize: 12, background: copied ? '#059669' : COLOR }}>
            {copied ? tr('onb.s3.copied') : tr('onb.s3.copy')}
          </button>
        </div>
        <p style={hint}>{tr('onb.s3.pasteHint')}</p>
        <button onClick={mailtoInformatyk} style={{ ...ghostBtn, color: COLOR, fontSize: 13, fontWeight: 600, padding: '8px 0', marginTop: 6 }}>
          ✉️ {tr('onb.s3.sendDev')}
        </button>
        {/* Alternatywa: modul PrestaShop (bez wklejania kodu) */}
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Masz PrestaShop?</div>
          <p style={{ ...hint, marginTop: 0, marginBottom: 8 }}>Zainstaluj moduł zamiast wklejać kod. Wgraj moduł w PrestaShop (Moduły → Załaduj moduł) i wklej sam token:</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <code style={{ flex: 1, fontSize: 11, background: '#f1f5f9', padding: '8px 10px', borderRadius: 6, wordBreak: 'break-all' as const }}>{widgetToken}</code>
            <button onClick={() => { navigator.clipboard.writeText(widgetToken); }} style={{ ...ghostBtn, color: COLOR, fontSize: 12, fontWeight: 600, padding: '8px 12px', whiteSpace: 'nowrap' as const }}>Kopiuj token</button>
          </div>
          <a href="https://aiopen-api.openkontakt.com/idealnysklep.zip" download style={{ display: 'inline-block', padding: '8px 16px', background: '#fff', color: COLOR, border: `1px solid ${COLOR}`, borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>⬇ Pobierz moduł PrestaShop</a>
        </div>
      </div>
    ),
    // Ekran 5 — Gotowe
    (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, margin: '4px 0 12px' }}>🎉</div>
        <h1 style={h1}>{tr('onb.s4.title')}</h1>
        <div style={{ textAlign: 'left', maxWidth: 380, margin: '18px auto 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[tr('onb.s4.point1'), tr('onb.s4.point2'), tr('onb.s4.point3')].map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: '#374151' }}>
              <span style={{ color: COLOR, flexShrink: 0 }}>✓</span><span>{p}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  ];

  // ── przyciski stopki per ekran ────────────────────────────────────────────────
  const renderFooterMain = () => {
    if (step === 0) return <button style={primaryBtn} onClick={() => next()}>{tr('onb.s0.cta')} →</button>;
    if (step === 1) return <button style={primaryBtn} disabled={saving} onClick={() => next({ shop_api_url: shopUrl || null, service_language: svcLang })}>{tr('onb.next')} →</button>;
    if (step === 2) return <button style={primaryBtn} disabled={saving} onClick={() => next()}>{tr('onb.next')} →</button>;
    if (step === 3) return <button style={primaryBtn} disabled={saving} onClick={() => next({ widget_proactive_msg: greeting, widget_assistant_name: assistantName, widget_assistant_first_name: assistantName })}>{tr('onb.s2.cta')} →</button>;
    if (step === 4) return <button style={primaryBtn} disabled={saving} onClick={() => next()}>{tr('onb.s3.cta')} →</button>;
    return <button style={primaryBtn} disabled={saving} onClick={finish}>{tr('onb.s4.cta')} →</button>;
  };

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={headerStyle}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
            {tr('onb.step')} {step + 1} {tr('onb.of')} {TOTAL}
          </span>
          <div style={{ flex: 1 }} />
          {dots}
        </div>

        <div style={bodyStyle}>
          {screens[step]}
        </div>

        <div style={footer}>
          {step < TOTAL - 1
            ? <button style={ghostBtn} onClick={skip}>{tr('onb.skip')}</button>
            : <span />}
          {renderFooterMain()}
        </div>
      </div>
    </div>
  );
}
